import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';

type ArchiveQueryType = 'alternate_reference' | 'lading_uuid';
type ArchiveThemeMode = 'dark' | 'light';

interface ArchiveDocument {
  id: number;
  document_uuid?: string;
  lading_uuid?: string;
  alternate_reference?: string;
  source_table?: string;
  file_name?: string;
  file_type?: string;
  file_format?: string;
  file_description?: string;
  document_date?: string;
  relative_path?: string;
  decoded_bytes?: number;
  sha256?: string;
  url?: string;
}

interface ArchiveLading {
  lading_uuid: string;
  alternate_reference?: string;
  lading_number?: string;
  lading_id?: string;
  pro_bill?: string;
  trip_number?: string;
  shipment_number?: string;
  customer_name?: string;
  created?: string;
  documents: ArchiveDocument[];
}

interface ArchiveSearchResponse {
  query: string;
  query_type: ArchiveQueryType;
  images_only: boolean;
  lading_count: number;
  document_count: number;
  ladings: ArchiveLading[];
}

@Component({
  selector: 'app-document-archive',
  templateUrl: './document-archive.component.html',
  styleUrls: ['./document-archive.component.scss'],
})
export class DocumentArchiveComponent implements OnInit {
  private readonly apiUrl = '/api/archive_docs.php';
  private readonly themeStorageKey = 'document-archive-theme';

  queryType: ArchiveQueryType = 'alternate_reference';
  query = '';
  imagesOnly = false;
  themeMode: ArchiveThemeMode = 'dark';
  isLoading = false;
  result: ArchiveSearchResponse | null = null;
  errorMessage = '';
  openingDocumentId: number | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.initializeTheme();

    const queryParams = this.route.snapshot.queryParamMap;
    const ladingUuid = queryParams.get('lading_uuid');
    const alternateReference = queryParams.get('alternate_reference') ?? queryParams.get('alt_ref');

    if (ladingUuid) {
      this.queryType = 'lading_uuid';
      this.query = ladingUuid;
    } else if (alternateReference) {
      this.queryType = 'alternate_reference';
      this.query = alternateReference;
    }

    this.imagesOnly = queryParams.get('images_only') === '1';

    if (this.query.trim() !== '') {
      this.search(false);
    }
  }

  get queryLabel(): string {
    return this.queryType === 'lading_uuid' ? 'Lading UUID' : 'Alternate Reference';
  }

  get queryPlaceholder(): string {
    return this.queryType === 'lading_uuid'
      ? '00025c3f-3c42-4be0-b037-2f190b7a5861'
      : '256305-01';
  }

  get ladingCount(): number {
    return this.result?.lading_count ?? 0;
  }

  get documentCount(): number {
    return this.result?.document_count ?? 0;
  }

  get ladings(): ArchiveLading[] {
    return this.result?.ladings ?? [];
  }

  get hasResults(): boolean {
    return this.ladings.length > 0;
  }

  get totalBytes(): number {
    return this.ladings.reduce((sum, lading) => {
      return sum + lading.documents.reduce((documentSum, document) => documentSum + (document.decoded_bytes ?? 0), 0);
    }, 0);
  }

  get resultLabel(): string {
    if (this.isLoading) {
      return 'Loading';
    }

    if (this.errorMessage) {
      return 'Error';
    }

    if (!this.result) {
      return 'Ready';
    }

    return this.documentCount > 0 ? 'Found' : 'No Docs';
  }

  get resultToneClass(): string {
    if (this.errorMessage) {
      return 'archive-tone--error';
    }

    if (!this.result || this.isLoading) {
      return 'archive-tone--idle';
    }

    return this.documentCount > 0 ? 'archive-tone--ready' : 'archive-tone--warning';
  }

  get resultIconClass(): string {
    if (this.errorMessage) {
      return 'fa-circle-xmark';
    }

    if (this.isLoading) {
      return 'fa-rotate fa-spin';
    }

    if (!this.result) {
      return 'fa-circle-info';
    }

    return this.documentCount > 0 ? 'fa-circle-check' : 'fa-triangle-exclamation';
  }

  setQueryType(queryType: ArchiveQueryType): void {
    this.queryType = queryType;
    this.errorMessage = '';
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  search(updateUrl = true): void {
    const trimmedQuery = this.query.trim();

    if (trimmedQuery === '') {
      this.errorMessage = `${this.queryLabel} is required.`;
      this.result = null;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const params = new HttpParams()
      .set('action', 'search')
      .set('query_type', this.queryType)
      .set('query', trimmedQuery)
      .set('images_only', this.imagesOnly ? '1' : '0');

    this.http.get<ArchiveSearchResponse>(this.apiUrl, { params })
      .pipe(finalize(() => {
        this.isLoading = false;
      }))
      .subscribe({
        next: (response) => {
          this.result = this.normalizeResponse(response);

          if (updateUrl) {
            this.persistSearchToUrl(trimmedQuery);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.result = null;
          this.errorMessage = this.readHttpError(error, 'Unable to load archived documents.');
        },
      });
  }

  clearSearch(): void {
    this.query = '';
    this.imagesOnly = false;
    this.errorMessage = '';
    this.result = null;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  openDocument(document: ArchiveDocument): void {
    if (!document.id) {
      this.errorMessage = 'Document id is missing.';
      return;
    }

    const popup = window.open('', '_blank');
    this.openingDocumentId = document.id;
    this.errorMessage = '';

    this.http.get(this.apiUrl, {
      params: new HttpParams()
        .set('action', 'file')
        .set('id', String(document.id))
        .set('filename', document.file_name ?? ''),
      observe: 'response',
      responseType: 'blob',
    })
      .pipe(finalize(() => {
        this.openingDocumentId = null;
      }))
      .subscribe({
        next: (response) => {
          this.openBlobResponse(response, popup);
        },
        error: (error: HttpErrorResponse) => {
          popup?.close();
          void this.readBlobError(error, 'Unable to open archived document.').then((message) => {
            this.errorMessage = message;
          });
        },
      });
  }

  formatBytes(bytes: number | undefined): string {
    if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
      return '';
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }

  formatDateText(value: string | undefined): string {
    if (!value) {
      return '';
    }

    return value.slice(0, 19);
  }

  trackByLadingUuid(_index: number, lading: ArchiveLading): string {
    return lading.lading_uuid;
  }

  trackByDocumentId(_index: number, document: ArchiveDocument): number {
    return document.id;
  }

  private persistSearchToUrl(query: string): void {
    const queryParams = this.queryType === 'lading_uuid'
      ? {
        lading_uuid: query,
        images_only: this.imagesOnly ? '1' : null,
      }
      : {
        alternate_reference: query,
        images_only: this.imagesOnly ? '1' : null,
      };

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private openBlobResponse(response: HttpResponse<Blob>, popup: Window | null): void {
    const blob = response.body;

    if (!blob) {
      popup?.close();
      this.errorMessage = 'Archive document response was empty.';
      return;
    }

    const contentType = response.headers.get('content-type') ?? blob.type;
    const fileBlob = contentType && blob.type !== contentType
      ? new Blob([blob], { type: contentType })
      : blob;
    const objectUrl = URL.createObjectURL(fileBlob);

    if (popup) {
      popup.location.href = objectUrl;
    } else {
      window.open(objectUrl, '_blank');
    }

    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }

  private readHttpError(error: HttpErrorResponse, fallback: string): string {
    if (error.error && typeof error.error === 'object' && !(error.error instanceof Blob)) {
      const maybeMessage = (error.error as { message?: unknown }).message;
      if (typeof maybeMessage === 'string' && maybeMessage.trim() !== '') {
        return maybeMessage;
      }
    }

    if (typeof error.error === 'string' && error.error.trim() !== '') {
      return error.error;
    }

    return fallback;
  }

  private async readBlobError(error: HttpErrorResponse, fallback: string): Promise<string> {
    if (error.error instanceof Blob) {
      const text = await error.error.text();
      if (text.trim() !== '') {
        try {
          const decoded = JSON.parse(text) as { message?: unknown };
          if (typeof decoded.message === 'string' && decoded.message.trim() !== '') {
            return decoded.message;
          }
        } catch {
          return text;
        }
      }
    }

    return this.readHttpError(error, fallback);
  }

  private normalizeResponse(response: ArchiveSearchResponse): ArchiveSearchResponse {
    return {
      ...response,
      ladings: Array.isArray(response.ladings)
        ? response.ladings.map((lading) => ({
          ...lading,
          documents: Array.isArray(lading.documents) ? lading.documents : [],
        }))
        : [],
    };
  }

  private initializeTheme(): void {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    this.themeMode = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
